import React, { useCallback, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import {
  CopyOutlined,
  DeleteOutlined,
  EllipsisOutlined,
  MenuOutlined
} from "@ant-design/icons";
import MainCard from "../../MainCard";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "../../../contexts/TranslationContext";
import { FormField } from "../../../api/types/form";
import { FormErrors, FormValues } from "../types";
import { buildChildConfig } from "../../../utils/form";
import { Draggable, DraggableProvided } from "react-beautiful-dnd";
import FormContextProvider from "../../../contexts/FormContext";
import { useDateFormatter } from "../../../utils/date";
import StrippedTypo from "../../nohtml/StrippedTypo";
import CmsContentConnect from "../../cms/CmsContentConnect";

type ModalChildrenEntryProps = {
  index: number;
  values?: FormValues;
  fieldConfig: FormField;
  removeRow: (index: number) => void;
  copyRow?: (index: number) => void;
  onValueChange?: (values: FormValues) => void;
  errors?: FormErrors;
  additionalFields?: Array<FormField>;
  withConnect?: boolean;
  connectOptions?: {
    name: string;
    label: string;
    help?: string;
    matcher?: { [input: string]: string };
  };
  locale?: string;
  sortable?: boolean;
};

const dateExp = new RegExp(/.{4}-.{2}-.{2}T.{2}:.{2}:.{2}/);

export default function ModalEntry({
                                     index,
                                     values,
                                     fieldConfig,
                                     removeRow,
                                     copyRow,
                                     onValueChange,
                                     errors,
                                     additionalFields = [],
                                     withConnect = false,
                                     connectOptions,
                                     locale,
                                     sortable = false
                                   }: ModalChildrenEntryProps) {
  const { translate } = useTranslation();
  const { formatDate } = useDateFormatter();
  const theme = useTheme();

  const now = new Date();

  const nestedBlocks = fieldConfig?.block_layout ?? false;

  const summarize = (values?: FormValues): string => {
    let response: Array<string | number> = [];
    for (const content of Object.values(values ?? {})) {
      if (
        (typeof content === "string" || typeof content === "number") &&
        !dateExp.test(`${content}`)
      ) {
        response.push(content);
      }
    }
    return response.join(" | ");
  };


  const ListItem = useCallback((provided?: DraggableProvided) => {
    const fields = buildChildConfig(
      fieldConfig.children ?? [],
      values ?? {}
    );

    const normalDates = Boolean(
      fieldConfig.children?.find(item => item.type === "datetime")
    );
    const hasDates = Boolean(
      normalDates ||
      additionalFields?.find(item => item.type === "datetime")
    );

    let isActive = false;
    let isFuture = false;
    if (hasDates && values) {
      isActive = Boolean(
        new Date(values.startDate).getTime() <= now.getTime() &&
        (typeof values.endDate === "undefined" ||
          !values.endDate ||
          new Date(values.endDate).getTime() >= now.getTime())
      );
      isFuture = Boolean(
        new Date(values.startDate).getTime() > now.getTime()
      );
    }

    return (
      <Accordion
        ref={provided?.innerRef}
        component="div"
        {...provided?.draggableProps}
        {...provided?.dragHandleProps}
        sx={{
          backgroundColor: theme.palette.background.default,
          borderTopStyle: "double",
          borderColor: theme.palette.error.main,
          borderWidth: errors ? 2 : 0,
          borderBottomStyle: "double"
        }}>
        <AccordionSummary>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}>
              {sortable && <MenuOutlined className="drag-hover" />}
              <Stack spacing={0}>
                <Typography
                  variant="subtitle1"
                  color={
                    isActive
                      ? "success.main"
                      : isFuture
                        ? "info.main"
                        : hasDates
                          ? "error.main"
                          : undefined
                  }>{`${translate(fieldConfig.label)} #${index + 1}`}</Typography>
                {hasDates && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}>
                    {values?.startDate && (
                      <Typography variant="subtitle2">
                        {translate(
                          "pages.cms.pages.time_publishing.start_date"
                        )}
                        :{" "}
                        {formatDate(
                          values.startDate
                        )}
                      </Typography>
                    )}
                    {values?.endDate && (
                      <>
                        <EllipsisOutlined
                          style={{
                            fontSize: 18
                          }}
                        />
                        <Typography variant="subtitle2">
                          {translate(
                            "pages.cms.pages.time_publishing.end_date"
                          )}
                          :{" "}
                          {formatDate(
                            values.endDate
                          )}
                        </Typography>
                      </>
                    )}
                  </Stack>
                )}
                <StrippedTypo variant="caption">
                  {summarize(values)}
                </StrippedTypo>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}>
              {copyRow && (
                <IconButton
                  key="copy"
                  color="info"
                  onClick={event => {
                    event.stopPropagation();
                    copyRow(index);
                  }}>
                  <CopyOutlined />
                </IconButton>
              )}
              <IconButton
                key="delete"
                color="error"
                onClick={event => {
                  event.stopPropagation();
                  removeRow(index);
                }}>
                <DeleteOutlined />
              </IconButton>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {nestedBlocks ? (
            <FormContextProvider
              formIndex={index}
              config={{
                name: fieldConfig.name,
                is_secured: false,
                block_layout: nestedBlocks,
                block_names:
                  fieldConfig?.block_names ?? [],
                block_sizes:
                  fieldConfig?.block_sizes ?? {},
                fields,
                additional_fields: additionalFields
              }}
              passedLocale={locale}
              onChange={onValueChange}
              errors={errors}
              formContent={
                withConnect ? (
                  <CmsContentConnect
                    {...connectOptions}
                  />
                ) : null
              }
              nested={true}
            />
          ) : (
            <MainCard sx={{ overflow: "visible" }}>
              <Stack spacing={2}>
                <FormContextProvider
                  formIndex={index}
                  config={{
                    name: fieldConfig.name,
                    is_secured: false,
                    block_layout: nestedBlocks,
                    block_names:
                      fieldConfig?.block_names ??
                      [],
                    block_sizes:
                      fieldConfig?.block_sizes ??
                      {},
                    fields,
                    additional_fields:
                    additionalFields
                  }}
                  onChange={onValueChange}
                  passedLocale={locale}
                  errors={errors}
                  formContent={
                    withConnect ? (
                      <CmsContentConnect
                        {...connectOptions}
                      />
                    ) : null
                  }
                  nested={true}
                />
              </Stack>
            </MainCard>
          )}
        </AccordionDetails>
      </Accordion>
    )
  }, [index, values])

  return useMemo(() => {
    if (!sortable) {
      return ListItem();
    }

    return (
      // @ts-ignore
      <Draggable
        draggableId={`modal-entry-sorted-${fieldConfig.name}-${index}`}
        index={index}>
        {provided => ListItem(provided)}
      </Draggable>
    );
  }, [sortable, fieldConfig.name, index, ListItem]);
}
